<template>
  <Section
    :id="id"
    :aria-label="t('rateShop.rateShop')"
    :loading="isLoading"
    class="order-rate-shop"
  >
    <BlockUI :blocked="isAcceptingQuote" full-screen />
    <OrderRateShopTabs
      v-for="shipmentSpecId of shipmentSpecIds"
      :key="shipmentSpecId"
      v-model:freight-allowance="freightAllowanceInternal"
      v-model:active-tab-key="activeTabKeyByShipmentSpec[shipmentSpecId]"
      :order="order"
      :shipment-spec-id="shipmentSpecId"
      :rate-shop="ratesByShipmentSpecs[shipmentSpecId]"
      :active-loadboards="loadboardsByShipmentSpec[shipmentSpecId]"
      :rate-shop-loading="
        !!quoteLoadingStateByShipmentSpec[shipmentSpecId]?.size
      "
      :view-uplift="canViewUplift"
      @post-to-loadboard="onPostToLoadboardClicked(shipmentSpecId)"
      @rate-quote-updated="onRateQuoteUpdated(shipmentSpecId, $event)"
      @rate-selected="onRateQuoteSelected(shipmentSpecId, $event)"
      @rate-shop-completed="onRateShopCompleted(shipmentSpecId)"
      @rate-shop-update="updateRateShopForShipmentSpec(shipmentSpecId)"
      @remove-loadboard="onRemoveLoadboard(shipmentSpecId, $event)"
      @request-spot-market-bid="emit('request-spot-market-bid', $event)"
      @send-to-brokerage="onSendToBrokerage(shipmentSpecId, $event)"
      @spot-market-cancelled="loadRateShopForShipmentSpec(shipmentSpecId)"
      @spot-quote-added="onSpotQuoteAdded(shipmentSpecId, $event)"
      @spot-quote-deleted="onSpotQuoteDeleted(shipmentSpecId, $event)"
      @waterfall-tender="onWaterfallTenderInitiated(shipmentSpecId, $event)"
    />

    <RateAssignAndTenderModal
      v-model:visible="rateSelectAndTenderModal.visible"
      :rate-quote="rateSelectAndTenderModal.rateQuote"
      :is-tender-only="rateSelectAndTenderModal.tenderOnly"
      @assign-and-tender="onAcceptRateQuoteAndTenderToCarrier"
      @assign-rate-only="
        onRateQuoteAssigned(rateSelectAndTenderModal.shipmentSpecId, $event)
      "
      @tender-to-carrier-only="onTenderToCarrier"
    />

    <EditBrokerageQuoteRedesignModal
      v-if="arApFeatureFlagEnabled"
      v-model:visible="brokerageInfo.isShowingModal"
      :brokerage-info="brokerageInfo"
      :brokerage-dates="brokerageDates"
      :view-uplift="canViewUplift"
      @confirm="onBrokerageConfirmed"
    />
    <BrokerageModal
      v-else
      v-model:visible="brokerageInfo.isShowingModal"
      :brokerage-info="brokerageInfo"
      :brokerage-dates="brokerageDates"
      @confirm="onBrokerageConfirmed"
    />

    <RateSelectionReasonModal
      v-model:visible="lowestCostReasonModal.visible"
      :lowest-price-quote="lowestPriceQuote"
      :selected-quote="lowestCostReasonModal.selectedQuote"
      @confirm="onRateSelectionConfirmed"
    />

    <PostToLoadboardModal
      v-model:visible="postToLoadboardModal.visible"
      :equipment-type="order.equipmentType"
      :requested-mode="order.requestedMode"
      :loading="postToLoadboardResponse.loading"
      :active-loadboards="postToLoadboardModal.activeLoadboards"
      @confirm="onPostToLoadboard"
    />

    <PostToLoadboardResultsModal
      v-model:visible="postToLoadboardResponseModalVisible"
      :responses="postToLoadboardResponse.data"
    />
  </Section>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useToast } from '@rygen/rygen-ui/composables';
import {
  ErrorResponse,
  Loadable,
  Optional,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  useFreightAllowance,
  useLoadboards,
  useOrderRateShop,
} from '@/composables';
import { useLowestCostReasonCode } from '@/composables/useLowestCostReasonCode.ts';
import i18n from '@/i18n';
import {
  AcceptQuotePayload,
  AcceptRateQuoteRequest,
  BrokerageDates,
  BrokeragePayload,
  FreightAllowance,
  Loadboard,
  LoadboardSource,
  Order,
  PostToLoadboardCommand,
  PostToLoadboardResponse,
  RateQuote,
  RateQuoteAcceptanceExceptionReason,
  RateSelectionReason,
  RateUsage,
  RoutingGuideClassification,
  TenderToCarrierRequest,
  WaterfallTenderConfig,
} from '@/model';
import { useSpotMarketStore } from '@/stores/SpotMarket.ts';
import { getErrorDetail, isBadRequest } from '@/util';

import OrderRateShopTabs, {
  type TabKey,
} from '@/components/order/rateShop/OrderRateShopTabs.vue';
import RateAssignAndTenderModal from '@/components/rateShop/assignAndTender/RateAssignAndTenderModal.vue';
import BrokerageModal from '@/components/rateShop/BrokerageModal.vue';
import EditBrokerageQuoteRedesignModal, {
  type BrokerageInfo,
} from '@/components/rateShop/editQuote/EditBrokerageQuoteRedesignModal.vue';
import PostToLoadboardModal from '@/components/rateShop/loadboard/PostToLoadboardModal.vue';
import PostToLoadboardResultsModal from '@/components/rateShop/loadboard/PostToLoadboardResultsModal.vue';
import RateSelectionReasonModal from '@/components/rateShop/RateSelectionReasonModal.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import BlockUI from 'primevue/blockui';

type RateSelectAndTenderTypes = {
  shipmentSpecId: number | undefined;
  rateQuote: RateQuote;
  tenderOnly: boolean;
  visible: boolean;
  rateSelectionReason?: RateSelectionReason;
};

const newBrokerageQuoteData = (
  rateQuote?: RateQuote,
  rateSelectionReason?: RateSelectionReason,
): BrokerageInfo => ({
  data: new RateQuote({
    ...rateQuote,
    source: 'BROKERAGE',
  }),
  dates: new BrokerageDates(),
  isShowingModal: false,
  shipmentSpecId: undefined,
  rateSelectionReason: rateSelectionReason,
  loading: false,
  error: undefined,
});

const props = defineProps({
  order: {
    type: Order,
    required: true,
  },
  id: {
    type: String,
    required: true,
  },
  /**
   * The Freight Allowance for the entity (e.g. Order, Shipment, ShipmentSpec) this rate shop represents
   */
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
  canViewUplift: {
    type: Boolean,
    default: () => false,
  },
});
const { order, id, freightAllowance, canViewUplift } = toRefs(props);
const emit = defineEmits<{
  (event: 'rate-assign-and-tender', rateQuote: RateQuote): void;
  (event: 'rate-shop-completed'): void;
  (event: 'request-spot-market-bid', shipmentSpecId: number): void;
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const { freightAllowanceInternal } = useFreightAllowance(
  freightAllowance,
  emit,
);
const { t } = useI18n();
const {
  addQuoteLoading,
  loadRelatedShipmentSpecsAndRates,
  loadRateShopForShipmentSpec,
  quoteLoadingStateByShipmentSpec,
  removeQuoteLoading,
  updateRateShopForShipmentSpec,
  ratesByShipmentSpecs,
  shipmentSpecs,
} = useOrderRateShop(order);
const { loadSpotMarkets } = useSpotMarketStore();
const router = useRouter();
const toast = useToast();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const { removeLoadboardFromShipmentSpec } = useLoadboards();

const arApFeatureFlagEnabled = useFlag('ar-ap-split-feat-flag');
const loadboardsEnabled = useFlag('loadboard-feat-flag');
const isAcceptingQuote = ref(false);
const loadboardsByShipmentSpec: Ref<Record<number, Loadable<Loadboard[]>>> =
  ref({});
const postToLoadboardModal: Ref<{
  visible: boolean;
  shipmentSpecId: number | undefined;
  activeLoadboards: LoadboardSource[];
}> = ref({ visible: false, shipmentSpecId: undefined, activeLoadboards: [] });
const postToLoadboardResponseModalVisible = ref(false);
const brokerageInfo: Ref<BrokerageInfo> = ref(newBrokerageQuoteData());
const postToLoadboardResponse: Ref<Loadable<PostToLoadboardResponse[]>> = ref({
  loading: false,
});

const rateSelectAndTenderModal: Ref<RateSelectAndTenderTypes> = ref({
  shipmentSpecId: undefined,
  rateQuote: new RateQuote(),
  tenderOnly: false,
  visible: false,
  rateSelectionReason: undefined,
});

const lowestCostReasonModal = ref({
  visible: false,
  selectedQuote: new RateQuote(),
  brokerageQuote: new RateQuote(),
  isBrokerage: false,
});

const activeTabKeyByShipmentSpec = ref<Record<number, TabKey>>({});

const shipmentSpecIds = computed((): number[] => {
  return shipmentSpecs.value.data?.map((x) => x.id).filter(notNull) ?? [];
});

const brokerageDates = computed(
  (): BrokerageDates =>
    new BrokerageDates({
      pickupTimeZone: order.value.origin?.location?.timeZone,
      pickupEarliest: order.value.origin?.requestedAppointmentEarliest,
      pickupLatest: order.value.origin?.requestedAppointmentLatest,
      deliveryTimeZone: order.value.destination?.location?.timeZone,
      deliveryEarliest: order.value.destination?.requestedAppointmentEarliest,
      deliveryLatest: order.value.destination?.requestedAppointmentLatest,
    }),
);

const isLoading = computed(
  () => shipmentSpecs.value.loading || brokerageInfo.value.loading,
);

const lowestPriceQuote = computed(() => {
  const shipmentSpecId = brokerageInfo.value.shipmentSpecId;
  if (!shipmentSpecId) {
    return undefined;
  }

  const rateShop = ratesByShipmentSpecs.value[shipmentSpecId];
  const rateQuotes = rateShop?.data?.rateQuotes ?? [];

  return rateQuotes
    .filter(
      (q) =>
        q.routingGuideClassification !== RoutingGuideClassification.EXCLUDED,
    )
    .filter((q) => q.rateUsage === RateUsage.RATE_SHOP)
    .sort((a, b) => (a.totalPrice ?? 0) - (b.totalPrice ?? 0))[0];
});

const showSpotMarketTab = (shipmentSpecId: number) => {
  activeTabKeyByShipmentSpec.value[shipmentSpecId] = 'SPOT_MARKETS';
};

const onSendToBrokerage = (
  shipmentSpecId: number,
  payload: Partial<AcceptQuotePayload>,
) => {
  brokerageInfo.value = newBrokerageQuoteData(
    payload.rateQuote,
    payload.rateSelectionReason,
  );
  brokerageInfo.value.shipmentSpecId = shipmentSpecId;
  brokerageInfo.value.isShowingModal = true;
};

const onBrokerageConfirmed = (brokeragePayload: BrokeragePayload) => {
  brokerageInfo.value.isShowingModal = false;
  brokerageInfo.value.dates = brokeragePayload.brokerageDates;
  if (brokerageInfo.value.shipmentSpecId) {
    if (
      !brokeragePayload.brokerageQuote.id &&
      isRateSelectionReasonRequired.value &&
      lowestPriceQuote.value
    ) {
      // when brokeragePayload.brokerageQuote.id is null this is the "send to brokerage" flow
      // we ALWAYS want to show the modal (unless there are no rate quotes to compare against)
      // if the user is selecting a brokerage that came through the rate shop,
      // the modal would have been shown the normal way
      lowestCostReasonModal.value.selectedQuote =
        brokeragePayload.brokerageQuote;
      lowestCostReasonModal.value.isBrokerage = true;
      lowestCostReasonModal.value.visible = true;
    } else {
      showRateAssignAndTenderModal(
        brokerageInfo.value.shipmentSpecId,
        brokeragePayload.brokerageQuote,
        brokerageInfo.value.rateSelectionReason,
      );
    }
  }
};

const onRateSelectionConfirmed = (payload: RateSelectionReason) => {
  if (!payload.rateSelectionReasonType) {
    return;
  }

  const rateSelectionReason = new RateSelectionReason(payload);

  if (brokerageInfo.value.shipmentSpecId) {
    showRateAssignAndTenderModal(
      brokerageInfo.value.shipmentSpecId,
      lowestCostReasonModal.value.selectedQuote,
      rateSelectionReason,
    );
  }

  // Reset modal state
  lowestCostReasonModal.value.visible = false;
  lowestCostReasonModal.value.isBrokerage = false;
};

const showRateAssignAndTenderModal = (
  shipmentSpecId: number,
  rateQuote: RateQuote,
  rateSelectionReason?: RateSelectionReason,
) => {
  rateSelectAndTenderModal.value = {
    shipmentSpecId: shipmentSpecId,
    rateQuote: rateQuote,
    tenderOnly: rateQuote.source === 'BROKERAGE',
    visible: true,
    rateSelectionReason: rateSelectionReason,
  };
};

const onTenderToCarrier = async (
  rateQuote: RateQuote,
  tenderMethod: TenderMethod,
  tenderConfigOptions: TenderConfigOptions,
) => {
  try {
    if (
      tenderMethod === TenderMethod.MCLEOD &&
      rateSelectAndTenderModal.value.shipmentSpecId
    ) {
      await onBrokerageAdded(rateSelectAndTenderModal.value.shipmentSpecId, {
        brokerageQuote: rateQuote,
        brokerageDates: brokerageInfo.value.dates,
        rateSelectionReason: rateSelectAndTenderModal.value.rateSelectionReason,
      });
      return;
    }
    if (
      !rateQuote.id &&
      rateQuote.source === 'BROKERAGE' &&
      rateSelectAndTenderModal.value.shipmentSpecId
    ) {
      rateQuote = await api.shipmentSpec.addSpotQuote(
        { id: rateSelectAndTenderModal.value.shipmentSpecId },
        rateQuote,
      );
    }
    sendTenderToCarrier(
      rateSelectAndTenderModal.value.shipmentSpecId,
      rateQuote,
      tenderMethod,
      tenderConfigOptions,
    );
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error accepting rate quote', e);
    const errorResponse = e.response?.data as ErrorResponse;
    const errorDetail = errorResponse.exceptionMessage;

    toast.addError({
      detail: t('rateShop.errorAcceptingRateQuoteParam', {
        param: errorDetail ? errorDetail : '',
      }),
      error: e,
    });
  }
};

const onAcceptRateQuoteAndTenderToCarrier = (
  rateQuote: RateQuote,
  tenderMethod: TenderMethod,
  tenderConfigOptions: TenderConfigOptions,
) => {
  sendTenderToCarrier(
    rateSelectAndTenderModal.value.shipmentSpecId,
    rateQuote,
    tenderMethod,
    tenderConfigOptions,
  );
};

const onRateQuoteSelected = async (
  shipmentSpecId: number | undefined,
  payload: AcceptQuotePayload,
) => {
  if (!shipmentSpecId) {
    throw new Error('RateQuote does not have a valid shipmentSpecId');
  }

  showRateAssignAndTenderModal(
    shipmentSpecId,
    payload.rateQuote,
    payload.rateSelectionReason,
  );
};

const onRateQuoteAssigned = async (
  shipmentSpecId: number | undefined,
  rateQuote: RateQuote,
) => {
  if (!shipmentSpecId) {
    throw new Error('RateQuote does not have a valid shipmentSpecId');
  }

  if (!rateQuote.id) {
    throw new Error('RateQuote does not have a valid id');
  }

  addQuoteLoading(shipmentSpecId, rateQuote.id);
  isAcceptingQuote.value = true;
  try {
    const request = new AcceptRateQuoteRequest({
      rateQuoteId: rateQuote.id,
      rateSelectionReason: rateSelectAndTenderModal.value.rateSelectionReason
        ? new RateSelectionReason({
            ...rateSelectAndTenderModal.value.rateSelectionReason,
            rateQuoteId: rateQuote.id,
          })
        : undefined,
    });

    const { shipmentId } = await api.shipmentSpec.acceptRateQuote(
      shipmentSpecId,
      request,
    );

    router
      .push({
        name: 'shipmentDetail',
        params: { shipmentId: `${shipmentId}` },
      })
      .then(() =>
        toast.addSuccess({
          detail: i18n.global.t('rateShop.successAcceptingRateQuote'),
        }),
      );
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    if (isBadRequest(e)) {
      const reasonCode = getErrorDetail(
        e,
        'reasonCode',
      ) as RateQuoteAcceptanceExceptionReason;
      if (reasonCode === RateQuoteAcceptanceExceptionReason.INACTIVE) {
        updateRateShopForShipmentSpec(shipmentSpecId);
      }
    }

    console.error('Error accepting rate quote', e);
    const errorResponse = e.response?.data as ErrorResponse;
    const errorDetail = errorResponse.exceptionMessage;

    toast.addError({
      detail: t('rateShop.errorAcceptingRateQuoteParam', {
        param: errorDetail ? errorDetail : '',
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, rateQuote.id);
    isAcceptingQuote.value = false;
  }
};

const onSpotQuoteAdded = async (
  shipmentSpecId: number,
  spotQuote: RateQuote,
) => {
  const quoteLoadingStateKey = spotQuote.id ?? Symbol();
  addQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
  try {
    spotQuote.laneQuoteData
      ? await api.shipmentSpec.addLaneQuote(shipmentSpecId, spotQuote)
      : await api.shipmentSpec.addSpotQuote({ id: shipmentSpecId }, spotQuote);
    await loadRateShopForShipmentSpec(shipmentSpecId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving spot quote', e);
    toast.addError({
      detail: t('errors.savingParam', {
        param: t('rateShop.spotQuotes'),
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
  }
};

const onRateQuoteUpdated = async (
  shipmentSpecId: number,
  rateQuote: RateQuote,
) => {
  const rateShop = ratesByShipmentSpecs.value[shipmentSpecId];
  if (!rateShop.data) {
    return;
  }

  if (!rateQuote.id) {
    throw new Error('rateQuote id is required to update rate quote');
  }

  addQuoteLoading(shipmentSpecId, rateQuote.id);
  try {
    await api.shipmentSpec.updateRateQuote({ id: shipmentSpecId }, rateQuote);
    loadRateShopForShipmentSpec(shipmentSpecId);

    if (rateQuote.source === 'SPOT_MARKET' && rateShop.data.id) {
      loadSpotMarkets(rateShop.data.id, {
        shipmentSpecId,
        bypassCache: true,
      });
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error updating rate quote');
    toast.addError({
      detail: t('errors.updatingParam', {
        param: t('rateShop.rateQuotes'),
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, rateQuote.id);
  }
};

const onSpotQuoteDeleted = async (
  shipmentSpecId: number,
  spotQuote: RateQuote,
) => {
  const rateShop = ratesByShipmentSpecs.value[shipmentSpecId];
  if (!rateShop.data) {
    return;
  }

  const quoteLoadingStateKey = spotQuote.id ?? Symbol();
  addQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
  try {
    await api.shipmentSpec.deleteSpotQuote({ id: shipmentSpecId }, spotQuote);
    await loadRateShopForShipmentSpec(shipmentSpecId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error deleting spot quote');
    toast.addError({
      detail: t('errors.deletingParam', {
        param: t('rateShop.spotQuotes'),
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
  }
};

const onBrokerageAdded = async (
  shipmentSpecId: number,
  { brokerageQuote, brokerageDates, rateSelectionReason }: BrokeragePayload,
) => {
  const quoteLoadingStateKey = brokerageQuote.id ?? Symbol();
  addQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
  isAcceptingQuote.value = true;
  try {
    const { shipmentId } = await api.shipmentSpec.addBrokerage(
      shipmentSpecId,
      brokerageQuote,
      brokerageDates,
      rateSelectionReason,
    );
    router
      .push({
        name: 'shipmentDetail',
        params: {
          shipmentId: `${shipmentId}`,
        },
      })
      .then(() =>
        toast.addSuccess({
          detail: t('messages.successSavingParam', {
            param: t('brokerages.brokerages'),
          }),
        }),
      )
      .finally(() => {
        removeQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
        isAcceptingQuote.value = false;
      });
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving brokerage quote', e);
    const errorResponse = e.response?.data as ErrorResponse;
    const errorDetail = errorResponse.exceptionMessage;

    if (isBadRequest(e)) {
      brokerageInfo.value.isShowingModal = true;
      brokerageInfo.value.error = errorResponse;
      return;
    }

    toast.addError({
      detail: t('errors.savingParam', {
        param:
          t('brokerages.brokerages') + (errorDetail ? ` - ${errorDetail}` : ''),
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
    isAcceptingQuote.value = false;
  }
};

const sendTenderToCarrier = async (
  shipmentSpecId: number | undefined,
  rateQuote: RateQuote,
  tenderMethod: TenderMethod,
  tenderConfigOptions: TenderConfigOptions,
) => {
  if (!shipmentSpecId) {
    throw new Error('RateQuote does not have a valid shipmentSpecId');
  }

  const quoteLoadingStateKey = rateQuote.id ?? Symbol();
  addQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
  isAcceptingQuote.value = true;

  try {
    const rateSelectionReason = rateSelectAndTenderModal.value
      .rateSelectionReason
      ? new RateSelectionReason({
          ...rateSelectAndTenderModal.value.rateSelectionReason,
          rateQuoteId: rateQuote.id,
        })
      : undefined;

    const tenderToCarrierRequest = new TenderToCarrierRequest(
      tenderMethod,
      tenderConfigOptions,
      rateSelectionReason,
    );

    if (rateQuote?.id) {
      const { shipmentId } = await api.shipmentSpec.acceptRateQuoteAndTender(
        shipmentSpecId,
        rateQuote.id,
        tenderToCarrierRequest,
      );

      router
        .push({
          name: 'shipmentDetail',
          params: { shipmentId: `${shipmentId}` },
        })
        .then(() =>
          toast.addSuccess({
            detail: i18n.global.t('rateShop.successAcceptingRateQuote'),
          }),
        );
    }
  } catch (e: any) {
    if (axios.isCancel(e)) {
      return;
    }

    if (isBadRequest(e)) {
      const reasonCode = getErrorDetail(
        e,
        'reasonCode',
      ) as RateQuoteAcceptanceExceptionReason;
      if (reasonCode === RateQuoteAcceptanceExceptionReason.INACTIVE) {
        updateRateShopForShipmentSpec(shipmentSpecId);
      }
    }

    console.error('Error accepting rate quote', e);
    const errorResponse = e.response?.data as ErrorResponse;
    const errorDetail = errorResponse.exceptionMessage;

    toast.addError({
      detail: t('rateShop.errorAcceptingRateQuoteParam', {
        param: errorDetail ? errorDetail : '',
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, quoteLoadingStateKey);
    isAcceptingQuote.value = false;
  }
};

const loadLoadboards = async (shipmentSpecId: number) => {
  if (!loadboardsByShipmentSpec.value[shipmentSpecId]) {
    loadboardsByShipmentSpec.value[shipmentSpecId] = { loading: false };
  }
  const loadboards = loadboardsByShipmentSpec.value[shipmentSpecId];
  try {
    loadboards.loading = true;
    loadboards.data = await api.shipmentSpecLoadboard.getAll(shipmentSpecId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    loadboards.error = e;
    console.error('Error loading active loadboards', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('rateShop.loadboards', 2),
      }),
      error: e,
    });
  } finally {
    loadboards.loading = false;
  }
};

const onRemoveLoadboard = async (
  shipmentSpecId: number,
  loadboardSource: LoadboardSource,
) => {
  const loadboard = loadboardsByShipmentSpec.value[shipmentSpecId]?.data?.find(
    (l) => loadboardSource === l.source,
  );
  if (!loadboard?.id) {
    console.error('No active loadboard found for', loadboardSource);
    toast.addError({
      detail: t('errors.removingParam', {
        param: t('rateShop.loadboards', 1),
      }),
      error: undefined,
    });
    return;
  }
  loadboardsByShipmentSpec.value[shipmentSpecId].loading = true;
  await removeLoadboardFromShipmentSpec(loadboard, { id: shipmentSpecId });
  await loadLoadboards(shipmentSpecId);
  loadboardsByShipmentSpec.value[shipmentSpecId].loading = false;
};

const onPostToLoadboardClicked = (shipmentSpecId: number) => {
  postToLoadboardModal.value.shipmentSpecId = shipmentSpecId;
  postToLoadboardModal.value.activeLoadboards =
    loadboardsByShipmentSpec.value[shipmentSpecId]?.data
      ?.map((l) => l.source)
      .filter(notNull) ?? [];
  postToLoadboardModal.value.visible = true;
};

const onPostToLoadboard = async (command: PostToLoadboardCommand) => {
  try {
    postToLoadboardResponse.value.loading = true;
    const shipmentSpecId = postToLoadboardModal.value.shipmentSpecId;
    if (shipmentSpecId == null) {
      throw new Error('ShipmentSpecId cannot be null');
    }
    command.entityId = shipmentSpecId;
    postToLoadboardResponse.value.data = await api.shipmentSpecLoadboard.post(
      shipmentSpecId,
      command,
    );
    postToLoadboardModal.value.visible = false;
    postToLoadboardResponseModalVisible.value = true;
    await loadLoadboards(shipmentSpecId);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error posting to load board', e);
    toast.addError({
      detail: t('rateShop.errorPostingToLoadboard'),
      error: e,
    });
  } finally {
    postToLoadboardResponse.value.loading = false;
  }
};

const onWaterfallTenderInitiated = async (
  shipmentSpecId: number,
  payload: {
    configuration: WaterfallTenderConfig;
    callback: (success: boolean) => void;
  },
) => {
  const loadingKey = Symbol();
  addQuoteLoading(shipmentSpecId, loadingKey);
  isAcceptingQuote.value = true;
  try {
    const { shipmentId } = await api.shipmentSpec.initiateWaterfallTender(
      shipmentSpecId,
      payload.configuration,
    );

    payload.callback(true);

    router
      .push({
        name: 'shipmentDetail',
        params: { shipmentId: `${shipmentId}` },
      })
      .then(() =>
        toast.addSuccess({
          detail: i18n.global.t('messages.successStartingParam', {
            param: t('waterfallTender.waterfallTender'),
          }),
        }),
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    payload.callback(false);
    console.error('Error initializing waterfall tender', e);
    toast.addError({
      detail: t('errors.startingParam', {
        param: t('waterfallTender.waterfallTender'),
      }),
      error: e,
    });
  } finally {
    removeQuoteLoading(shipmentSpecId, loadingKey);
    isAcceptingQuote.value = false;
  }
};

const onRateShopCompleted = (shipmentSpecId: number) => {
  if (loadboardsEnabled.value) {
    loadLoadboards(shipmentSpecId);
  }
  emit('rate-shop-completed');
};

watch(
  () => brokerageInfo.value.isShowingModal,
  (newVal) => {
    if (!newVal) {
      brokerageInfo.value.error = undefined;
    }
  },
);

watch(
  shipmentSpecIds,
  (newIds) => {
    if (!loadboardsEnabled.value) {
      return;
    }
    newIds.forEach(loadLoadboards);
  },
  { immediate: true },
);

watch(
  () => ({ id: order.value?.id, ownerId: order.value.owner?.id }),
  (
    newVal: { id?: number; ownerId?: number },
    oldVal: Optional<{ id?: number; ownerId?: number }>,
  ) => {
    const idChanged = newVal.id !== oldVal?.id;
    const ownerChanged = newVal.ownerId !== oldVal?.ownerId;

    if (idChanged || ownerChanged) {
      loadRelatedShipmentSpecsAndRates();
    }
  },
  { immediate: true, deep: true },
);

defineExpose({ showSpotMarketTab });
</script>
